import { operatorApiFetch } from './apiService';

export const governanceService = {
  // 訂閱相關
  getSubscription: async () => {
    const response = await operatorApiFetch('governance/subscription');
    return response.json();
  },

  updateSubscription: async (data) => {
    const response = await operatorApiFetch('governance/subscription', {
      method: 'PATCH',
      body: JSON.stringify({ subscription: data })
    });
    return response.json();
  },

  // 新用戶首次訂閱
  subscribeNewPlan: async (pricingPlanId, billingCycle) => {
    const response = await operatorApiFetch('governance/subscription/subscribe', {
      method: 'POST',
      body: JSON.stringify({
        pricing_plan_id: pricingPlanId,
        billing_cycle: billingCycle
      })
    });
    return response.json();
  },

  upgradeSubscription: async (pricingPlanId, billingCycle) => {
    const response = await operatorApiFetch('governance/subscription/upgrade', {
      method: 'POST',
      body: JSON.stringify({
        pricing_plan_id: pricingPlanId,
        billing_cycle: billingCycle
      })
    });
    return response.json();
  },

  downgradeSubscription: async (pricingPlanId) => {
    const response = await operatorApiFetch('governance/subscription/downgrade', {
      method: 'POST',
      body: JSON.stringify({ pricing_plan_id: pricingPlanId })
    });
    return response.json();
  },

  changeBillingCycle: async (billingCycle) => {
    const response = await operatorApiFetch('governance/subscription/change_billing_cycle', {
      method: 'POST',
      body: JSON.stringify({ billing_cycle: billingCycle })
    });
    return response.json();
  },

  cancelSubscription: async (feedback, effectiveImmediately = false) => {
    const response = await operatorApiFetch('governance/subscription/cancel', {
      method: 'POST',
      body: JSON.stringify({
        feedback: feedback,
        effective_immediately: effectiveImmediately
      })
    });
    return response.json();
  },

  reactivateSubscription: async () => {
    const response = await operatorApiFetch('governance/subscription/reactivate', {
      method: 'POST'
    });
    return response.json();
  },

  // 帳戶重啟（suspended → active）
  getReactivationPreview: async () => {
    const response = await operatorApiFetch('governance/account/reactivation_preview');
    return response.json();
  },

  reactivateAccount: async () => {
    const response = await operatorApiFetch('governance/account/reactivate', {
      method: 'POST'
    });
    return response.json();
  },

  cancelPendingChange: async () => {
    const response = await operatorApiFetch('governance/subscription/cancel_pending_change', {
      method: 'POST'
    });
    return response.json();
  },

  // 取消年繳升級，恢復月繳
  revertBillingCycleUpgrade: async () => {
    const response = await operatorApiFetch('governance/subscription/revert_billing_cycle_upgrade', {
      method: 'POST'
    });
    return response.json();
  },

  // 方案相關
  getPricingPlans: async () => {
    const response = await operatorApiFetch('governance/pricing_plans');
    return response.json();
  },

  getPricingPlan: async (id) => {
    const response = await operatorApiFetch(`governance/pricing_plans/${id}`);
    return response.json();
  },

  // 用量相關
  getUsageRecords: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);

    const queryString = queryParams.toString();
    const url = queryString ? `governance/usage_records?${queryString}` : 'governance/usage_records';
    const response = await operatorApiFetch(url);
    return response.json();
  },

  getUsageSummary: async () => {
    const response = await operatorApiFetch('governance/usage_records/summary');
    return response.json();
  },

  // 付款方式相關
  getPaymentMethods: async () => {
    const response = await operatorApiFetch('governance/payment_methods');
    return response.json();
  },

  // 取得 SDK Token (PayUNi UNi Embed)
  // 用於前端載入嵌入式付款元件進行信用卡綁定
  getSdkToken: async (returnUrl = null, amount = 1) => {
    const response = await operatorApiFetch('governance/payment_methods/sdk_token', {
      method: 'POST',
      body: JSON.stringify({
        return_url: returnUrl,
        amount: amount
      })
    });
    return response.json();
  },

  // 完成信用卡綁定 (PayUNi UNi Embed)
  // 前端 getTradeResult() 成功後調用此 API 完成幕後交易授權
  bindCard: async (sdkToken, setDefault = false) => {
    const response = await operatorApiFetch('governance/payment_methods/bind_card', {
      method: 'POST',
      body: JSON.stringify({
        sdk_token: sdkToken,
        set_default: setDefault
      })
    });
    return response.json();
  },

  removePaymentMethod: async (id) => {
    const response = await operatorApiFetch(`governance/payment_methods/${id}`, {
      method: 'DELETE'
    });
    return response.json();
  },

  setDefaultPaymentMethod: async (id) => {
    const response = await operatorApiFetch(`governance/payment_methods/${id}/set_default`, {
      method: 'POST'
    });
    return response.json();
  },

  // 發票相關
  getInvoices: async () => {
    const response = await operatorApiFetch('governance/invoices');
    return response.json();
  },

  getInvoice: async (id) => {
    const response = await operatorApiFetch(`governance/invoices/${id}`);
    return response.json();
  },

  // 建立付款請求（銀行轉帳）
  // 產生待付款發票，顯示匯款資訊
  createCheckout: async (pricingPlanId, billingCycle, paymentMethod = 'bank_transfer') => {
    const response = await operatorApiFetch('governance/invoices/checkout', {
      method: 'POST',
      body: JSON.stringify({
        pricing_plan_id: pricingPlanId,
        billing_cycle: billingCycle,
        payment_method: paymentMethod
      })
    });
    return response.json();
  },

  // 試算方案變更：回試算金額 + 將被作廢的未付 subscription invoice 清單
  // FE 收到 requires_confirmation=true 時須先彈確認 modal，使用者點確定後才呼叫 upgrade
  previewPlanChange: async (pricingPlanId, billingCycle) => {
    const response = await operatorApiFetch('governance/subscription/preview_change', {
      method: 'POST',
      body: JSON.stringify({ pricing_plan_id: pricingPlanId, billing_cycle: billingCycle })
    });
    return response.json();
  },

  // 信用卡付款（pending/past_due invoice）
  payInvoice: async (invoiceId) => {
    const response = await operatorApiFetch(`governance/invoices/${invoiceId}/pay`, {
      method: 'POST'
    });
    return response.json();
  },

  // 取消未付款帳單
  cancelInvoice: async (invoiceId) => {
    const response = await operatorApiFetch(`governance/invoices/${invoiceId}/cancel`, {
      method: 'POST'
    });
    return response.json();
  },

  // 回報匯款完成
  // 客戶完成銀行轉帳後，回報匯款資訊以利對帳
  notifyPayment: async (invoiceId, data) => {
    const response = await operatorApiFetch(`governance/invoices/${invoiceId}/notify_payment`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
    return response.json();
  },

  // 帳單資料與發票設定
  getBillingProfile: async () => {
    const response = await operatorApiFetch('governance/billing_profile');
    return response.json();
  },

  updateBillingProfile: async (data) => {
    const response = await operatorApiFetch('governance/billing_profile', {
      method: 'PATCH',
      body: JSON.stringify({ billing_profile: data })
    });
    return response.json();
  },

  // PCS Ledger 相關
  getPcsLedgerEntries: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.order_id) queryParams.append('order_id', params.order_id);
    if (params.event_type) queryParams.append('event_type', params.event_type);
    if (params.page) queryParams.append('page', params.page);
    if (params.per_page) queryParams.append('per_page', params.per_page);

    const queryString = queryParams.toString();
    const url = queryString ? `governance/pcs_ledger_entries?${queryString}` : 'governance/pcs_ledger_entries';
    const response = await operatorApiFetch(url);
    return response.json();
  },

  getPcsLedgerSummary: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);

    const queryString = queryParams.toString();
    const url = queryString ? `governance/pcs_ledger_entries/summary?${queryString}` : 'governance/pcs_ledger_entries/summary';
    const response = await operatorApiFetch(url);
    return response.json();
  },

  exportPcsLedger: async (params = {}) => {
    const response = await operatorApiFetch('governance/pcs_ledger_entries/export', {
      method: 'POST',
      body: JSON.stringify({
        start_date: params.start_date,
        end_date: params.end_date
      })
    });
    return response.json();
  },

  // ==================== AI Wallet ====================

  updateDailyBudget: async (limit) => {
    const response = await operatorApiFetch('governance/ai_wallet/update_daily_budget', {
      method: 'POST',
      body: JSON.stringify({ daily_budget_limit: limit })
    });
    return response.json();
  },

  // ==================== AI 帳戶加值 (Credit Purchase) ====================

  // 取得可用的加值方案
  getAiCreditPacks: async () => {
    const response = await operatorApiFetch('governance/ai_credit_purchases/packs');
    return response.json();
  },

  // AI 帳戶加值 (confirm: false 為預覽, confirm: true 為確認加值)
  purchaseAiCredits: async (packId, confirm = false) => {
    const response = await operatorApiFetch('governance/ai_credit_purchases', {
      method: 'POST',
      body: JSON.stringify({
        pack_id: packId,
        confirm: confirm
      })
    });
    return response.json();
  },

  // 取得加值歷史紀錄
  getAiCreditPurchaseHistory: async () => {
    const response = await operatorApiFetch('governance/ai_credit_purchases');
    return response.json();
  },

  // ==================== Addon 相關 ====================

  // 取得可購買的 addon 選項
  getAddonOptions: async () => {
    const response = await operatorApiFetch('governance/subscription/addon_options');
    return response.json();
  },

  // 取得已購買的 addons
  getAddons: async () => {
    const response = await operatorApiFetch('governance/subscription/addons');
    return response.json();
  },

  // 購買 addon (confirm: false 為預覽, confirm: true 為確認購買)
  purchaseAddon: async (addonType, quantity, confirm = false) => {
    const response = await operatorApiFetch('governance/subscription/purchase_addon', {
      method: 'POST',
      body: JSON.stringify({
        addon_type: addonType,
        quantity: quantity,
        confirm: confirm
      })
    });
    return response.json();
  },

  // 減少 addon 數量（addonId 指定要減量的那一筆 addon）
  reduceAddon: async (addonType, quantity, addonId = null) => {
    const response = await operatorApiFetch('governance/subscription/reduce_addon', {
      method: 'POST',
      body: JSON.stringify({
        addon_type: addonType,
        quantity: quantity,
        addon_id: addonId
      })
    });
    return response.json();
  },

  // 取消 addon 自動續約（addonId 指定要取消的那一筆 addon）
  cancelAddon: async (addonType, addonId = null) => {
    const response = await operatorApiFetch('governance/subscription/cancel_addon', {
      method: 'POST',
      body: JSON.stringify({
        addon_type: addonType,
        addon_id: addonId
      })
    });
    return response.json();
  },

  // 業務推薦代碼
  bindSalesReferralCode: async (code) => {
    const response = await operatorApiFetch('governance/subscription/sales_referral_code', {
      method: 'PATCH',
      body: JSON.stringify({ sales_referral_code: code })
    });
    return response.json();
  }
};

export default governanceService;
